import { extractLocalMediaPoster, posterDataUrl } from '../utils/frameExtractor';
import { describeUploadFile, transferResumableFile } from './resumableUpload';
import { apiService, getApiErrorMessage, ImportJob, LocalUploadDescriptor, ResumableUploadResponse } from './api';
import { processingRealtimeStore } from './processingRealtimeStore';
import { generateIdempotencyKey } from './idempotency';
import { safeLocalStorageGet, safeLocalStorageSet } from '../utils/safeStorage';
import { isSupportedLocalMediaFile, exceedsMediaUploadSizeLimit, MEDIA_UPLOAD_QUEUE_MAX_ITEMS, MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE } from '../utils/mediaUploadPolicy';
import type { GoogleDrivePickedFile } from '../components/GoogleDrivePickerDialog';

type UploadState = 'selected' | 'needs_file' | 'needs_drive' | 'uploading' | 'saving' | 'processing' | 'completed' | 'failed';
export interface UploadQueueItem {
  id: string; key: string; source: 'local' | 'drive' | 'url'; name: string; size: number; type: string;
  indexId: string; indexName: string; title: string; state: UploadState; bytes: number; confirmed: number;
  createdAt: number; jobId?: string; descriptor?: LocalUploadDescriptor; driveId?: string; resourceKey?: string;
  phase?: string; error?: string; videoIds?: string[]; job?: ImportJob;
  restartRequired?: boolean;
  thumbnailData?: string;
}
interface QueueSnapshot { items: UploadQueueItem[]; open: boolean; indexId: string; indexName: string; storageAvailable: boolean }
const terminal = (state: UploadState) => state === 'completed' || state === 'failed';

export { describeUploadFile } from './resumableUpload';

/** The transfer owner survives route changes; persisted records never contain file bytes or capabilities. */
export class UploadQueue {
  private userId: string | null = null;
  private generation = 0;
  private snapshot: QueueSnapshot = { items: [], open: false, indexId: '', indexName: '', storageAvailable: true };
  private listeners = new Set<() => void>();
  private files = new Map<string, File>();
  private posters = new Map<string, Promise<Blob | null>>();
  private posterTransfers = new Map<string, Promise<void>>();
  private posterControllers = new Map<string, AbortController>();
  private urlAttempts = new Map<string, { id: string; key: string }>();
  private tokens = new Map<string, string>();
  private controllers = new Map<string, AbortController>();
  private unsubscribeProgress: (() => void) | undefined;
  private observedJobs = '';
  private finalizing = new Set<string>();
  private running = 0;
  private pending: string[] = [];
  private restartRequests = new Set<string>();

  getSnapshot = () => this.snapshot;
  getContextKey = () => `${this.generation}:${this.snapshot.indexId}`;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private persist() {
    if (!this.userId) return;
    const items = this.snapshot.items.map(({ job, thumbnailData, ...item }) => ({ ...item, bytes: item.confirmed }));
    const available = safeLocalStorageSet(`media-upload-queue:${this.userId}`, JSON.stringify({ ...this.snapshot, items }));
    this.snapshot = { ...this.snapshot, storageAvailable: available };
  }
  private publish(persist = true) {
    if (persist) this.persist();
    this.listeners.forEach((listener) => listener());
    this.observe();
  }
  private patch(id: string, patch: Partial<UploadQueueItem>, persist = true) {
    this.snapshot = { ...this.snapshot, items: this.snapshot.items.map((item) => item.id === id ? { ...item, ...patch } : item) };
    this.publish(persist);
  }
  activateUserScope(userId: string | null) {
    if (this.userId === userId) return;
    this.generation += 1;
    this.controllers.forEach((controller) => controller.abort());
    this.posterControllers.forEach((controller) => controller.abort());
    this.posterControllers.clear(); this.posters.clear(); this.posterTransfers.clear(); this.urlAttempts.clear();
    this.controllers.clear(); this.files.clear(); this.tokens.clear(); this.pending = []; this.running = 0;
    this.restartRequests.clear();
    this.unsubscribeProgress?.(); this.unsubscribeProgress = undefined; this.observedJobs = ''; this.finalizing.clear();
    this.userId = userId;
    this.snapshot = { items: [], open: false, indexId: '', indexName: '', storageAvailable: true };
    if (userId) {
      try {
        const saved = JSON.parse(safeLocalStorageGet(`media-upload-queue:${userId}`) || 'null');
        if (saved && Array.isArray(saved.items)) {
          const items = saved.items.filter((item: UploadQueueItem) => item?.id && item.key && item.indexId).slice(-50).map((item: UploadQueueItem) => ({
            ...item, state: ['selected', 'uploading'].includes(item.state) ? (item.source === 'drive' ? 'needs_drive' : 'needs_file') : item.state,
          }));
          this.snapshot = { ...this.snapshot, open: saved.open === true, indexId: saved.indexId || '', indexName: saved.indexName || '', items };
        }
      } catch { /* Invalid local metadata cannot override server state. */ }
    }
    this.publish(false);
    this.refreshJobs();
  }
  private refreshJobs() {
    const generation = this.generation;
    this.snapshot.items.filter((item) => item.jobId && !terminal(item.state)).forEach((item) => {
      void apiService.getImportJob(item.jobId!).then(async (job) => {
        if (generation !== this.generation) return;
        this.applyJob(item.id, job);
        if (item.state === 'saving' && job.status === 'awaiting_upload') await this.finishUpload(item.id, generation);
      }).catch(() => { if (generation === this.generation) this.patch(item.id, { error: 'Could not refresh this upload. Reopen Uploads to retry.' }); });
    });
  }
  open(indexId: string, indexName: string) {
    this.snapshot = { ...this.snapshot, open: true, indexId, indexName }; this.publish();
    if (this.snapshot.items.some((item) => item.error && item.jobId && !terminal(item.state))) this.refreshJobs();
    processingRealtimeStore.refresh();
  }
  close() { this.snapshot = { ...this.snapshot, open: false }; this.publish(); }
  addFiles(files: File[]) {
    if (!this.userId || !this.snapshot.indexId) throw new Error('Select an index before uploading.');
    const active = this.snapshot.items.filter((item) => !terminal(item.state)).length;
    if (active + files.length > MEDIA_UPLOAD_QUEUE_MAX_ITEMS) throw new Error(`Upload up to ${MEDIA_UPLOAD_QUEUE_MAX_ITEMS} files at a time.`);
    files.forEach((file) => {
      if (!isSupportedLocalMediaFile(file)) throw new Error(`${file.name} is not a supported media file.`);
      if (exceedsMediaUploadSizeLimit(file.size)) throw new Error(MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE);
    });
    const items = files.map((file): UploadQueueItem => {
      const id = crypto.randomUUID(); this.files.set(id, file);
      return { id, key: generateIdempotencyKey('media-upload'), source: 'local', name: file.name, size: file.size, type: file.type,
        indexId: this.snapshot.indexId, indexName: this.snapshot.indexName, title: file.name, state: 'selected', bytes: 0, confirmed: 0, createdAt: Date.now() };
    });
    this.snapshot = { ...this.snapshot, items: [...this.snapshot.items.slice(-45), ...items] }; this.publish();
    items.forEach((item) => this.preparePoster(item.id, this.files.get(item.id)!));
  }
  private preparePoster(id: string, file: File) {
    if (this.posters.has(id)) return;
    const generation = this.generation;
    const poster = extractLocalMediaPoster(file).catch(() => null);
    this.posters.set(id, poster);
    void poster.then(posterDataUrl).then((thumbnailData) => {
      if (generation === this.generation && thumbnailData) this.patch(id, { thumbnailData }, false);
    });
  }
  private transferPoster(id: string, jobId: string, generation: number) {
    const poster = this.posters.get(id);
    if (!poster || this.posterTransfers.has(id)) return;
    const controller = new AbortController(); this.posterControllers.set(id, controller);
    const pending = poster.then(async (blob) => {
      if (blob && generation === this.generation && !controller.signal.aborted) {
        await apiService.uploadResumablePoster(jobId, blob, controller.signal);
      }
    }).catch(() => {
      // The canonical ingestion worker still owns the required poster if browser decoding or transfer fails.
    }).finally(() => {
      if (generation === this.generation) { this.posterControllers.delete(id); this.posters.delete(id); }
    });
    this.posterTransfers.set(id, pending);
  }
  async importUrls(urls: string[]) {
    if (!this.userId || !this.snapshot.indexId) throw new Error('Select an index before importing.');
    const cleaned = Array.from(new Set(urls.map((url) => url.trim()).filter(Boolean)));
    if (!cleaned.length) throw new Error('Enter at least one media URL.');
    if (cleaned.some((value) => { try { return new URL(value).protocol !== 'https:'; } catch { return true; } })) {
      throw new Error('Enter one HTTPS media URL per line.');
    }
    const attemptScope = JSON.stringify([this.snapshot.indexId, cleaned]);
    const attempt = this.urlAttempts.get(attemptScope) || { id: crypto.randomUUID(), key: generateIdempotencyKey('media-url-import') };
    this.urlAttempts.set(attemptScope, attempt);
    const generation = this.generation;
    const item: UploadQueueItem = { ...attempt, source: 'url', name: cleaned.length === 1 ? new URL(cleaned[0]).hostname : `${cleaned.length} media URLs`,
      title: '', type: '', size: 0, indexId: this.snapshot.indexId, indexName: this.snapshot.indexName,
      state: 'processing', phase: 'Starting import', bytes: 0, confirmed: 0, createdAt: Date.now() };
    this.snapshot = { ...this.snapshot, items: [...this.snapshot.items.filter((row) => row.id !== item.id).slice(-49), item] }; this.publish();
    try {
      const job = await apiService.importMediaUrls(cleaned, item.indexId, item.key);
      if (generation !== this.generation) return;
      this.applyJob(item.id, job); this.urlAttempts.delete(attemptScope);
    } catch (error) {
      if (generation === this.generation) this.patch(item.id, { state: 'failed', error: getApiErrorMessage(error, 'Import could not be started.') });
      throw error;
    }
  }
  addDriveFiles(files: GoogleDrivePickedFile[], token: string) {
    if (!this.userId || !this.snapshot.indexId) throw new Error('Select an index before uploading.');
    if (this.snapshot.items.filter((item) => !terminal(item.state)).length + files.length > MEDIA_UPLOAD_QUEUE_MAX_ITEMS) throw new Error(`Upload up to ${MEDIA_UPLOAD_QUEUE_MAX_ITEMS} files at a time.`);
    if (files.some((file) => exceedsMediaUploadSizeLimit(file.sizeBytes))) throw new Error(MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE);
    const items = files.map((file): UploadQueueItem => {
      const id = crypto.randomUUID(); this.tokens.set(id, token);
      return { id, key: generateIdempotencyKey('drive-upload'), source: 'drive', name: file.name, size: file.sizeBytes, type: file.mimeType,
        indexId: this.snapshot.indexId, indexName: this.snapshot.indexName, title: file.name, state: 'selected', bytes: 0, confirmed: 0,
        driveId: file.id, resourceKey: file.resourceKey, createdAt: Date.now() };
    });
    this.snapshot = { ...this.snapshot, items: [...this.snapshot.items.slice(-45), ...items] }; this.publish();
  }
  setTitle(id: string, title: string) { this.patch(id, { title }); }
  remove(id: string) {
    const item = this.snapshot.items.find((row) => row.id === id);
    if (!item || this.controllers.has(id) || item.state === 'processing') return;
    this.files.delete(id); this.tokens.delete(id); this.posters.delete(id); this.posterTransfers.delete(id);
    this.snapshot = { ...this.snapshot, items: this.snapshot.items.filter((row) => row.id !== id) }; this.publish();
  }
  async reselect(id: string, file: File) {
    const generation = this.generation;
    const item = this.snapshot.items.find((row) => row.id === id);
    if (!item) return;
    const descriptor = await describeUploadFile(file);
    if (generation !== this.generation) return;
    if (item.descriptor && JSON.stringify(item.descriptor) !== JSON.stringify(descriptor)) throw new Error('Select the same unchanged file to resume this upload.');
    if (!item.descriptor && (file.name !== item.name || file.size !== item.size)) throw new Error('Select the same file to restore this upload.');
    this.files.set(id, file); this.preparePoster(id, file); this.patch(id, { descriptor, state: 'selected', error: undefined }); this.start(id);
  }
  reconnectDrive(id: string, token: string) { this.tokens.set(id, token); this.patch(id, { state: 'selected', error: undefined }); this.start(id); }
  canRestart(id: string) { return this.files.has(id); }
  restart(id: string) {
    if (!this.files.has(id)) return;
    this.restartRequests.add(id); this.patch(id, { state: 'selected', error: undefined }); this.start(id);
  }
  start(id?: string) {
    const ids = this.snapshot.items.filter((item) => item.source !== 'url' && (!id || item.id === id) && ['selected', 'failed'].includes(item.state)).map((item) => item.id);
    ids.forEach((key) => { if (!this.pending.includes(key) && !this.controllers.has(key)) this.pending.push(key); });
    this.drain();
  }
  private drain() {
    while (this.running < 3 && this.pending.length) {
      const id = this.pending.shift()!;
      const generation = this.generation;
      this.running += 1;
      void this.transfer(id, generation).finally(() => {
        if (generation === this.generation) { this.running -= 1; this.drain(); }
      });
    }
  }
  private async transfer(id: string, generation: number) {
    let item = this.snapshot.items.find((row) => row.id === id);
    if (!item || !this.userId) return;
    const controller = new AbortController(); this.controllers.set(id, controller);
    const check = () => { if (generation !== this.generation || controller.signal.aborted) throw new DOMException('Upload paused.', 'AbortError'); };
    try {
      if (item.source === 'drive') {
        const token = this.tokens.get(id);
        if (!token) { this.patch(id, { state: 'needs_drive' }); return; }
        this.patch(id, { state: 'uploading', phase: 'Copying from Drive', error: undefined });
        if (!item.jobId) {
          const response = await apiService.initiateDriveUpload(item.driveId!, token, item.indexId, item.title, item.key, item.resourceKey); check();
          this.patch(id, { jobId: response.job.job_id, job: response.job }); item = { ...item, jobId: response.job.job_id };
        }
        this.observe();
        const job = await apiService.captureDriveUpload(item.jobId!, token, item.resourceKey, controller.signal); check(); this.applyJob(id, job);
      } else {
        const file = this.files.get(id);
        if (!file) { this.patch(id, { state: 'needs_file' }); return; }
        const descriptor = item.descriptor || await describeUploadFile(file); check();
        this.patch(id, { descriptor, state: 'uploading', phase: 'preparing_upload', error: undefined });
        const restartExpired = this.restartRequests.delete(id);
        const response: ResumableUploadResponse = item.jobId
          ? await apiService.resumeResumableUpload(item.jobId, descriptor, restartExpired)
          : await apiService.initiateResumableUpload(descriptor, item.title, item.indexId, item.key);
        check(); this.patch(id, { jobId: response.job.job_id, job: response.job, restartRequired: false });
        this.transferPoster(id, response.job.job_id, generation);
        const session = response.upload;
        if (!session) { this.applyJob(id, response.job); return; }
        this.patch(id, { phase: 'uploading' });
        await transferResumableFile(file, session, controller.signal, (bytes, confirmed) => {
          check(); this.patch(id, { bytes, confirmed }, bytes === confirmed);
        });
        check();
        this.patch(id, { state: 'saving', phase: 'Saving' });
        await this.finishUpload(id, generation);
      }
    } catch (error: any) {
      if (generation !== this.generation) return;
      this.patch(id, { state: item.source === 'drive' ? 'needs_drive' : 'needs_file',
        ...(item.source === 'local' ? { bytes: this.snapshot.items.find((row) => row.id === id)?.confirmed || 0 } : {}),
        restartRequired: error?.response?.data?.error?.code === 'upload_session_expired',
        error: getApiErrorMessage(error, 'Transfer interrupted. Resume this file to continue.') });
    } finally {
      if (generation === this.generation) { this.controllers.delete(id); this.tokens.delete(id); }
    }
  }
  private async finishUpload(id: string, generation: number) {
    const item = this.snapshot.items.find((row) => row.id === id);
    if (!item?.jobId) return;
    const job = await apiService.completeResumableUpload(item.jobId);
    if (generation === this.generation) this.applyJob(id, job);
  }
  private applyJob(id: string, job: ImportJob) {
    const item = this.snapshot.items.find((row) => row.id === id);
    if (!item) return;
    const copyingDrive = item.source === 'drive' && ['copying_from_drive', 'saving'].includes(job.phase || '')
      && Date.parse(job.liveness?.lease_expires_at || '') > Date.now();
    const state: UploadState = job.status === 'completed' ? 'completed' : ['failed', 'cancelled'].includes(job.status) ? 'failed'
      : job.status === 'awaiting_upload' ? copyingDrive ? 'uploading'
        : item.source === 'drive' && !this.controllers.has(id) ? 'needs_drive' : item.state : 'processing';
    this.patch(id, { job, jobId: job.job_id, state, phase: job.phase,
      error: job.error_message || (item.source === 'local' && job.status === 'awaiting_upload' ? item.error : undefined),
      ...(item.source === 'drive' ? { bytes: job.progress?.bytes_confirmed || 0, confirmed: job.progress?.bytes_confirmed || 0,
        size: job.progress?.bytes_total || item.size } : {}),
    });
    if (job.status === 'completed' && !item.videoIds?.length && !this.finalizing.has(id)) {
      this.finalizing.add(id);
      const generation = this.generation;
      void apiService.getAllImportJobOutputs(job.job_id).then(async (outputs) => {
        const ids = outputs.flatMap((output) => output.video_id ? [output.video_id] : []);
        const videos = [];
        for (let offset = 0; offset < ids.length; offset += 100) videos.push(...await apiService.getBatchVideoSummaries(ids.slice(offset, offset + 100)));
        if (generation !== this.generation) return;
        this.patch(id, { videoIds: ids }); this.files.delete(id);
        processingRealtimeStore.publishImportedVideos(item.indexId === 'playground' ? null : item.indexId, videos);
      }).catch(() => { if (generation === this.generation) this.patch(id, { error: 'Uploaded. Open the index to load its media.' }); })
        .finally(() => { if (generation === this.generation) this.finalizing.delete(id); });
    }
  }
  private observe() {
    const rows = this.snapshot.items.filter((item) => item.jobId && (item.state === 'processing'
      || (item.source === 'drive' && (item.state === 'uploading'
        || (item.state === 'needs_drive' && ['copying_from_drive', 'saving'].includes(item.phase || ''))))));
    const ids = rows.map((item) => item.jobId!).sort(); const key = ids.join(',');
    if (key === this.observedJobs) return;
    this.unsubscribeProgress?.(); this.unsubscribeProgress = undefined; this.observedJobs = key;
    if (!ids.length) return;
    this.unsubscribeProgress = processingRealtimeStore.subscribe((event) => {
      if (event.event_type !== 'import_job.progress') return;
      const item = this.snapshot.items.find((row) => row.jobId === event.payload?.job_id);
      if (item && event.payload.snapshot) this.applyJob(item.id, { ...item.job, ...event.payload.snapshot });
    }, { import_job_ids: ids }, (connection) => {
      if (connection !== 'idle') return;
      this.snapshot.items.filter((item) => item.source === 'drive' && item.state === 'uploading'
        && !this.controllers.has(item.id) && !(Date.parse(item.job?.liveness?.lease_expires_at || '') > Date.now()))
        .forEach((item) => this.patch(item.id, { state: 'needs_drive', phase: 'reconnect_drive' }));
    });
  }
}

export const uploadQueue = new UploadQueue();
